import { useEffect, useLayoutEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { useStoreActions, useStoreState } from '@/stores/store';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { BodyWrapper } from '@/layouts/BodyWrapper';
import { PatientProfileHeader } from '@/views/PatientProfileHeader';
import { PatientProfileSets } from '@/views/PatientProfileSets';
import { PatientProfileSettingsCard } from '@/views/PatientProfileSettingsCard';
import { Icon } from '@/components/Icon';
import { Card } from '@/components/ui/therapist/Card';
import {
  dismissLicenseWarning,
  snoozeLicenseWarning,
  fetchPatient,
  getPatientLicenseStatus,
} from '@/api/services/patient';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { Patient } from '@/stores/store.types';
import { LicenseBanner } from './LicenseBanner';
import { LicenseStatus } from '@/api/services/user';
import { showWarningCheck } from '@/utils/licenseCheck';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { logError } from '@/utils/logHandling';

export function PatientProfile(
  props: StackScreenProps<TherapistStackParamList, 'PatientProfile'>,
) {
  const { id, patients } = props.route.params;
  const { changeID, changeSelectedPatient, fetchPatients } =
    useDashboardContext();
  const { triggerRefresh } = useStoreActions((actions) => ({
    triggerRefresh: actions.triggerRefresh,
  }));
  const { isTherapistDemo } = useStoreState((store) => store);
  const { showNotification } = useNotification();
  const { navigation } = props;
  const isFocused = useIsFocused();
  const selectedPatient = patients?.find((patient) => patient._id === id);
  const [localSelectedPatient, setLocalSelectedPatient] = useState<
    Patient | undefined
  >(patients?.find((patient) => patient._id === id));

  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(
    null,
  );
  const [showLicenseWarning, setShowLicenseWarning] = useState<boolean | null>(
    null,
  );

  const {
    textsets,
    sentencesets,
    wordsets,
    _id,
    firstName,
    lastName,
    profileColor,
    diagnosis,
    code,
    created,
    activeTextsets,
    activeSentencesets,
    activeWordsets,
    biography,
    gender,
    therapistTags,
  } = localSelectedPatient ?? {};

  const syncSelectedPatient = () => {
    if (localSelectedPatient) {
      changeSelectedPatient(localSelectedPatient);
      changeID(localSelectedPatient._id, true, patients);
    }
  };

  const loadLicenseStatus = async () => {
    const license = await getPatientLicenseStatus(id);

    setLicenseStatus(license);

    if (!license || license.daysLeft === undefined) {
      return;
    }

    if (license.isDismissed) {
      setShowLicenseWarning(false);

      return;
    }

    if (!license.snoozedAt && showWarningCheck(license)) {
      setShowLicenseWarning(true);

      return;
    }

    if (
      license.snoozedAt &&
      new Date().getTime() - new Date(license.snoozedAt).getTime() >
        24 * 60 * 60 * 1000
    ) {
      setShowLicenseWarning(true);

      return;
    }
  };

  const refreshPatient = async () => {
    const hasInternet = await hasInternetAsync();
    if (!hasInternet) {
      return;
    }

    const refreshedPatient = await fetchPatient(id);
    setLocalSelectedPatient(refreshedPatient);
  };

  useEffect(() => {
    syncSelectedPatient();
  }, [id, patients, localSelectedPatient]);

  useEffect(() => {
    if (isFocused) {
      refreshPatient();
      triggerRefresh();
      loadLicenseStatus();
    }
  }, [isFocused]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <AppHeaderAction
          primary
          onPress={() => navigation.navigate('Home')}
          icon={<Icon name="chevronLeft" />}
          text="Zurück"
        />
      ),
    });
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: `Profil: ${firstName} ${lastName}`,
    });
  }, [firstName, lastName]);

  const onRemindTomorrow = async () => {
    try {
      if (isTherapistDemo) {
        showNotification({
          type: 'error',
          title: 'In der Demo nicht möglich',
          position: 'top',
        });
        return;
      }
      await snoozeLicenseWarning(localSelectedPatient?._id!);
      setShowLicenseWarning(false);
    } catch (error) {
      showNotification({
        title: 'Fehler beim Pausieren',
        message:
          'Die Erinnerung konnte nicht pausiert werden. Bitte versuchen Sie es erneut.',
        type: 'error',
      });
      logError('Failed to snooze license warning', error);
    }
  };

  const onHideForever = async () => {
    try {
      if (isTherapistDemo) {
        showNotification({
          type: 'error',
          title: 'In der Demo nicht möglich',
          position: 'top',
        });
        return;
      }
      await dismissLicenseWarning(localSelectedPatient?._id!);
      setShowLicenseWarning(false);
    } catch (error) {
      showNotification({
        title: 'Fehler beim Deaktivieren',
        message:
          'Die Erinnerung konnte nicht deaktiviert werden. Bitte versuchen Sie es erneut.',
        type: 'error',
      });
      logError('Failed to dismiss license warning', error);
    }
  };

  return (
    <BodyWrapper bodyStyle={{ width: '90%' }}>
      <ScrollView style={{ width: '100%' }}>
        <View style={{ gap: 32 }}>
          {!!licenseStatus && showLicenseWarning !== null ? (
            <LicenseBanner
              showLicenseWarning={
                !!showLicenseWarning && showWarningCheck(licenseStatus)
              }
              license={{ daysLeft: licenseStatus.daysLeft }}
              onRemindTomorrow={onRemindTomorrow}
              onHideForever={onHideForever}
              licenseType={licenseStatus.type}
            />
          ) : null}

          <Card styleBody={{ padding: 0 }}>
            <PatientProfileHeader
              profileColor={profileColor ?? ''}
              _id={_id!}
              fullName={(firstName ?? '') + ' ' + (lastName ?? '')}
              biography={biography ?? ''}
              diagnosis={diagnosis ?? ''}
              code={code ?? ''}
              gender={gender ?? ''}
              created={created ?? ''}
              therapistTags={therapistTags}
              fetchPatients={fetchPatients}
              navigation={navigation}
            />
          </Card>
          <View>
            <Card title="Eigentraining">
              <PatientProfileSettingsCard
                selectedPatient={selectedPatient}
                selectedID={_id}
                activeTextsets={activeTextsets ?? []}
                activeSentencesets={activeSentencesets ?? []}
                activeWordsets={activeWordsets ?? []}
              />
            </Card>
          </View>
          <View>
            <PatientProfileSets
              patientId={id}
              textsets={textsets ?? []}
              wordsets={wordsets ?? []}
              sentencesets={sentencesets ?? []}
              refreshPatient={refreshPatient}
            />
          </View>
        </View>
      </ScrollView>
    </BodyWrapper>
  );
}
